import { useEffect } from "react";

import { isAxiosError } from "axios";

import { FeatBitIdentity, identifyAll, resetToAnonymous, useGetCarbonProgramByFarmId } from "utils";

type IdentityInput = {
  id?: string | null;
  email?: string | null;
  farmId?: string | null;
  country?: string | null;
  extraAttributes?: Record<string, string | number | boolean>;
};

/**
 * Identifies the current user on both FeatBit projects. When `user` is null/undefined,
 * resets the clients to an anonymous identity (e.g. on sign-out).
 */
export const useIdentifyFeatureFlags = (user: IdentityInput | null | undefined) => {
  const userId = user?.id ?? null;
  const email = user?.email ?? null;
  const farmId = user?.farmId ?? null;
  const extraKey = user?.extraAttributes ? JSON.stringify(user.extraAttributes) : "";
  const { data: carbonProgram, isSuccess, isError, error } = useGetCarbonProgramByFarmId(farmId ?? undefined);
  // The API answers 418 when the farm has no carbon program, so that error still counts as a loaded result
  const hasNoCarbonProgram = isAxiosError(error) && error.response?.status === 418;
  const hasCarbonProgramLoaded = isSuccess || hasNoCarbonProgram;
  const hasCarbonProgramSettled = isSuccess || isError;
  const carbonProgramSlug = carbonProgram?.programSlug ?? null;
  const country = user?.country ?? null;

  useEffect(() => {
    if (!userId) {
      resetToAnonymous();
      return;
    }

    // Wait for the carbon-program request to settle so we don't identify twice with different attributes
    if (farmId && !hasCarbonProgramSettled) return;

    const attributes: Record<string, string | number | boolean> = extraKey ? JSON.parse(extraKey) : {};
    if (farmId) attributes.farmId = farmId;
    // TODO (Alp): This is a quick hack to know if there is a carbon program or not. Find a better solution after the beta release!
    if (hasCarbonProgramLoaded && farmId) attributes.carbonProgram = carbonProgramSlug ?? "none";
    attributes.country = country ?? "none";

    const identity: FeatBitIdentity = {
      keyId: userId,
      name: email ?? userId,
      attributes: Object.keys(attributes).length ? attributes : undefined,
    };
    identifyAll(identity);
  }, [userId, email, farmId, carbonProgramSlug, extraKey, country, hasCarbonProgramLoaded, hasCarbonProgramSettled]);
};
